// React imports
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@saleshandy/design-system';

// ** SVG Imports
import { DialerEndCall } from '../../../svg/dialer-endCall';
import UnknownProspect from '../../../svg/unknown-prospect';
import DialerControlsHeader from './dialer-controls-header';
import { DialerHoldEnable } from '../../../svg/dialer-hold';
import {
  DialerMicrophoneActive,
  DialerMicrophoneEnable,
} from '../../../svg/dialer-microphone';
import { DialerRecordEnable } from '../../../svg/dialer-record';
import { DialerVoicemail } from '../../../svg/dialer-voicemail';
// ** Custom Imports
import { CallControlIconProps, DialerRingingProps } from '../types';
import { getInitials } from '../utils/helper';
import {
  callRecordingRequest,
  dropVoicemailTemplateRequest,
  getVoicemailTemplateMinimalListRequest,
} from '../../../../components/dialer/extra-actions';
import { CallRecordingAction } from '../../../../components/dialer/types';
import {
  DialerControlsState,
  resetCallRecordingRequest,
  setDuringCallNote,
  toggleDialerControl,
} from '../../../../components/dialer/dialer-slice';
import { RootState } from '../../../../store/root-reducer';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../utils';
import VoicemailTemplateDropdown, {
  VoicemailTemplateOption,
} from './voicemail-template-dropdown';
import toaster from '../../../toaster';
import { KeypadDots } from '../../../svg/keypad-dots';

const VOICEMAIL_TEMPLATE_PAGE_SIZE = 25;

const MicrophoneIcon: React.FC<CallControlIconProps> = ({
  isActive,
  className = '',
}) => {
  if (isActive) {
    return <DialerMicrophoneActive width={44} height={44} />;
  }
  return (
    <DialerMicrophoneEnable width={20} height={20} className={className} />
  );
};

/**
 * @default
 * @description This component is used to display the dialer ringing UI
 * @param onCallDisconnect - Function to disconnect the call
 * @param initiatedCallId - The ID of the initiated call
 * @param selectedProspect - The selected prospect
 * @returns JSX element
 */
const DialerRinging: React.FC<DialerRingingProps> = ({
  onCallDisconnect,
  initiatedCallId,
  selectedProspect,
  selectedCountryCode,
  ongoingPhoneNumber,
  callManagementState,
  controlsState,
  endInitiatedCall,
  holdCallHandlers,
  dropVoicemailTemplateRequestStatus,
  inboundCallPhoneNumber = '',
}) => {
  const dispatch = useDispatch();
  const dialerPopupRef = useRef<HTMLDivElement>(null);
  const voiceMailTemplateList = useSelector(
    (state: RootState) => state?.dialer?.voicemailTemplateMinimalList,
  );
  const getVoicemailTemplateMinimalListRequestStatus = useSelector(
    (state: RootState) =>
      state?.dialer?.getVoicemailTemplateMinimalListRequest?.status,
  );

  const initiateCallRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.initiateCallRequest?.status,
  );
  const callRecordingRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.callRecordingRequest,
  );
  const duringCallNote = useSelector(
    (state: RootState) => state?.dialer?.duringCallNote,
  );
  // Controlled textarea must receive a string; null/undefined would make React treat
  // the field inconsistently vs the grey placeholder text.
  const duringCallNoteValue = duringCallNote ?? '';
  const voiceMailTemplateListOptions: VoicemailTemplateOption[] =
    voiceMailTemplateList?.templates?.map((item) => ({
      key: String(item?.id),
      name: item?.title ?? '',
      value: String(item?.id),
    })) ?? [];

  const [selectedTemplate, setSelectedTemplate] = useState<
    string | undefined
  >();
  const toggleControl = (control: keyof DialerControlsState) => {
    dispatch(toggleDialerControl(control));
    if (control === 'record' && initiatedCallId) {
      // Dispatch recording action here, e.g.:
      dispatch(
        callRecordingRequest({
          callId: Number(initiatedCallId),
          action: controlsState[control]
            ? CallRecordingAction?.Stop
            : CallRecordingAction?.Start,
        }),
      );
    }
  };

  const handleCallHold = useCallback(() => {
    try {
      if (controlsState?.hold) {
        holdCallHandlers?.unholdCall();
        toggleControl('hold');
        toaster.success('Call resumed successfully');
      } else {
        holdCallHandlers?.holdCall();
        toggleControl('hold');
        toaster.success('Call hold successfully');
      }
    } catch (err) {
      toaster.error(
        controlsState?.hold ? 'Failed to resume call' : 'Failed to hold call',
      );
      // Do not toggle - keep UI in sync with actual call state
    }
  }, [controlsState?.hold, holdCallHandlers, toggleControl]);

  const handleCallMute = useCallback(() => {
    if (!callManagementState?.call) return;
    try {
      if (controlsState?.microphone) {
        callManagementState?.call?.mute(false);
        toggleControl('microphone');
        toaster.success('Call unmuted');
      } else {
        callManagementState?.call?.mute(true);
        toggleControl('microphone');
        toaster.success('Call muted');
      }
    } catch (err) {
      toaster.error(
        controlsState?.microphone ? 'Failed to unmute' : 'Failed to mute',
      );
    }
  }, [callManagementState?.call, controlsState?.microphone, toggleControl]);

  const prospectName =
    `${selectedProspect?.firstName?.trim() || ''} ${
      selectedProspect?.lastName?.trim() || ''
    }`?.trim() || 'Unknown Prospect';

  const dialedPhoneNumber = useMemo(() => {
    let number = '';
    if (callManagementState?.callDirection === 'inbound') {
      number = inboundCallPhoneNumber || '';
    } else {
      number = ongoingPhoneNumber
        ? `${selectedCountryCode}${ongoingPhoneNumber?.trim()}`
        : inboundCallPhoneNumber || '';
    }
    return number;
  }, [
    selectedCountryCode,
    ongoingPhoneNumber,
    inboundCallPhoneNumber,
    callManagementState?.callDirection,
  ]);

  const handleSendDropVoiceMailSelectedTemplate = useCallback(() => {
    if (initiatedCallId && selectedTemplate) {
      dispatch(
        dropVoicemailTemplateRequest({
          callId: Number(initiatedCallId),
          voicemailTemplateId: Number(selectedTemplate),
        }),
      );
    }
  }, [initiatedCallId, selectedTemplate]);

  const handleLoadMoreVoicemailTemplates = useCallback(() => {
    const loadedCount = voiceMailTemplateList?.templates?.length ?? 0;
    const nextPage = Math.floor(loadedCount / VOICEMAIL_TEMPLATE_PAGE_SIZE) + 1;
    dispatch(
      getVoicemailTemplateMinimalListRequest({
        pageNumber: nextPage,
        pageSize: VOICEMAIL_TEMPLATE_PAGE_SIZE,
      }),
    );
  }, [dispatch, voiceMailTemplateList?.templates?.length]);

  const voicemailTemplateHasMore = useMemo(() => {
    const templates = voiceMailTemplateList?.templates ?? [];
    const total = voiceMailTemplateList?.total ?? 0;
    return templates.length < total;
  }, [voiceMailTemplateList]);

  const voicemailTemplateLoadingMore = useMemo(
    () =>
      getIsRequestPending(getVoicemailTemplateMinimalListRequestStatus) &&
      (voiceMailTemplateList?.templates?.length ?? 0) > 0,
    [
      getVoicemailTemplateMinimalListRequestStatus,
      voiceMailTemplateList?.templates?.length,
    ],
  );

  useEffect(() => {
    dispatch(
      getVoicemailTemplateMinimalListRequest({
        pageNumber: 1,
        pageSize: VOICEMAIL_TEMPLATE_PAGE_SIZE,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    executeOnRequestStatus({
      status: callRecordingRequestStatus?.status,
      onSuccess: () => {
        toaster.success(callRecordingRequestStatus?.message);
        dispatch(resetCallRecordingRequest());
      },
    });
  }, [callRecordingRequestStatus?.status]);

  useEffect(() => {
    dispatch(setDuringCallNote(null));
  }, []);

  return (
    <div
      ref={dialerPopupRef}
      className={`dialer-ringing-container ${
        controlsState.voicemail ? 'voicemail-active' : ''
      }`}
    >
      <div className="dialer-ringing-controls-wrapper">
        <div
          className={`dialer-ringing-controls ${
            endInitiatedCall ? 'end-initiated-call' : ''
          }`}
        >
          <DialerControlsHeader
            avatarContent={
              prospectName === 'Unknown Prospect' ||
              prospectName === dialedPhoneNumber ? (
                <UnknownProspect width={40} height={40} />
              ) : (
                getInitials(prospectName)
              )
            }
            name={
              prospectName === 'Unknown Prospect'
                ? dialedPhoneNumber
                : prospectName
            }
            phone={dialedPhoneNumber}
            icon={<DialerEndCall width={24} height={24} />}
            onIconClick={onCallDisconnect}
            disabled={
              getIsRequestPending(initiateCallRequestStatus) ||
              callManagementState?.callStatus === 'initiated'
            }
          />
          <div className="dialer-ringing-controls-btns">
            <Button
              type="button"
              onClick={handleCallMute}
              disabled={
                callManagementState?.callTwilioStatus !== 'connected' ||
                controlsState?.hold
              }
            >
              <MicrophoneIcon
                isActive={controlsState.microphone}
                className={`dialer-ringing-microphone-icon ${
                  controlsState?.microphone ? 'active' : ''
                }`}
              />
            </Button>
            <Button
              type="button"
              onClick={handleCallHold}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              disabled={
                callManagementState?.callTwilioStatus !== 'connected' ||
                controlsState?.microphone ||
                controlsState?.keypad
              }
            >
              <DialerHoldEnable
                width={24}
                height={24}
                className={`dialer-ringing-functionality-icon ${
                  controlsState?.hold ? 'active' : ''
                }`}
              />
            </Button>
            <Button
              type="button"
              onClick={() => toggleControl('record')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              disabled={callManagementState?.callTwilioStatus !== 'connected'}
            >
              <DialerRecordEnable
                width={24}
                height={24}
                className={`dialer-ringing-functionality-icon ${
                  controlsState.record ? 'active' : ''
                }`}
              />
            </Button>
            <Button
              onClick={() => toggleControl('voicemail')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              disabled={
                callManagementState?.callTwilioStatus !== 'connected' ||
                controlsState.keypad
              }
            >
              <DialerVoicemail
                width={24}
                height={24}
                className={`dialer-ringing-functionality-icon ${
                  controlsState.voicemail ? 'active' : ''
                }`}
              />
            </Button>
            {/* Dialpad */}
            <Button
              onClick={() => toggleControl('keypad')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              disabled={
                callManagementState?.callTwilioStatus !== 'connected' ||
                controlsState.hold ||
                controlsState.voicemail
              }
            >
              <span
                className={`dialer-ringing-keypad-icon-container ${
                  controlsState.keypad ? 'active' : ''
                } ${
                  callManagementState?.callTwilioStatus !== 'connected'
                    ? 'disabled'
                    : ''
                }`}
              >
                <KeypadDots
                  width={20}
                  height={20}
                  className="dialer-ringing-keypad-icon"
                />
              </span>
            </Button>
          </div>
        </div>
      </div>
      {!controlsState?.voicemail ? (
        <div className="dialer-ringing-note-wrapper">
          <textarea
            placeholder="Type here to add note."
            value={duringCallNoteValue}
            onChange={(e) => dispatch(setDuringCallNote(e.target.value))}
          />
        </div>
      ) : (
        <div className="dialer-ringing-voicemail-drop-container">
          <div
            // ref={voicemailContainerRef}
            className="dialer-ringing-voicemail-container"
          >
            <p className="dialer-ringing-voicemail-instruction">
              Speak your message or pick one from the templates below.
            </p>
            <div className="dialer-ringing-voicemail-form">
              <span className="dialer-ringing-voicemail-label">
                Voicemail Template
              </span>
              <VoicemailTemplateDropdown<VoicemailTemplateOption>
                showSearch={true}
                selectedValue={selectedTemplate}
                onSelectTemplate={(template) => {
                  setSelectedTemplate(template.key);
                }}
                options={voiceMailTemplateListOptions}
                optionRenderer={(option) => (
                  <span className="voicemail-template-option">
                    {option.name}
                  </span>
                )}
                filterOption={(value, option) =>
                  option?.name?.toLowerCase().includes(value.toLowerCase())
                }
                searchPlaceholder="Search"
                container={dialerPopupRef.current}
                hasMore={voicemailTemplateHasMore}
                onLoadMore={handleLoadMoreVoicemailTemplates}
                isLoadingMore={voicemailTemplateLoadingMore}
              />
            </div>
          </div>
          <div className="dialer-ringing-voicemail-actions">
            <Button
              {...accessibleOnClick(() =>
                handleSendDropVoiceMailSelectedTemplate(),
              )}
              disabled={
                !selectedTemplate ||
                getIsRequestPending(dropVoicemailTemplateRequestStatus)
              }
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DialerRinging);
